import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { DeployRefreshService } from '../services/deploy-refresh/deploy-refresh.service';
import { ToastService } from '../services/toast/toast.service';

/**
 * GlobalErrorHandler
 *
 * Catches uncaught exceptions application-wide, logs them, and
 * presents a toast notification to the user to prevent silent failures.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  /**
   * Creates an instance of GlobalErrorHandler.
   * @param injector - Angular injector to resolve services dynamically
   */
  constructor(private injector: Injector) {}

  /**
   * Catches and processes any uncaught application error.
   * Displays a user-friendly toast message and logs the stack trace to the console.
   *
   * @param error - Caught exception or error object
   */
  handleError(error: unknown): void {
    // Session expiry / intentional logout — do not toast "Http failure … (unknown url)".
    if (this.isBenignAuthError(error)) {
      console.warn('Auth session ended:', error);
      return;
    }

    // A deploy removed the hashed file this tab is trying to open. Reload once.
    const deployRefresh = this.injector.get(DeployRefreshService);
    if (deployRefresh.reloadIfStaleBuild(error)) {
      return;
    }

    console.error('Unhandled Exception Caught:', error);

    const ngZone = this.injector.get(NgZone);
    ngZone.run(() => {
      try {
        const toast = this.injector.get(ToastService);
        const err = error as { message?: string };
        const errorMessage = err?.message || String(error) || 'An unexpected error occurred';
        toast.showError('System Error', errorMessage);
      } catch (dispatchError) {
        console.error('Failed to dispatch error toast:', dispatchError);
      }
    });
  }

  /**
   * Whether the error is an expected auth/session failure that should not toast.
   * @param error - Caught value from Angular's ErrorHandler
   * @returns True when the error is a 401 HTTP response (often synthetic after logout)
   */
  private isBenignAuthError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }
    const httpError = error as { status?: number; message?: string };
    if (httpError.status === 401) {
      return true;
    }
    const message = httpError.message ?? '';
    return (
      message.includes('(unknown url)') &&
      message.toLowerCase().includes('unauthorized')
    );
  }
}
